import { useQuery } from '@tanstack/react-query';
import { adminApi, type PushLogEntry } from '@/lib/adminApi';
import { DataTable, Spinner, EmptyState, type Column } from '@/components/ui';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';

/** Short human label for the FCM reason on a failed/no-token push. */
function reasonOf(r: PushLogEntry): string {
  if (r.ok) return '';
  if (!r.token_present) return 'No device registered';
  const body = (r.provider_response || r.error || '').trim();
  // Surface the FCM error code if present (e.g. UNREGISTERED / INVALID_ARGUMENT),
  // else the raw reason. provider_status 401/403 = an OAuth auth wedge.
  const m = body.match(/"status"\s*:\s*"([A-Z_]+)"/) || body.match(/\b(UNREGISTERED|INVALID_ARGUMENT|SENDER_ID_MISMATCH|UNAVAILABLE|INTERNAL)\b/);
  if (m) return m[1];
  if (r.provider_status === 401 || r.provider_status === 403) return `auth ${r.provider_status} (token wedge)`;
  if (r.provider_status > 0) return `HTTP ${r.provider_status}`;
  return body.slice(0, 80) || 'failed';
}

const columns: Array<Column<PushLogEntry>> = [
  {
    key: 'created_at',
    header: 'When',
    cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span>,
  },
  {
    key: 'scope',
    header: 'Recipient',
    cell: (r) => (
      <span className="metric">
        {r.scope || '—'} #{r.recipient_id || '—'}
      </span>
    ),
  },
  {
    key: 'kind',
    header: 'Type',
    cell: (r) => <span className="text-xs">{r.kind || 'notification'}</span>,
  },
  {
    key: 'token_present',
    header: 'Device',
    cell: (r) =>
      r.token_present ? (
        <span className="text-xs" style={{ color: 'var(--ok, #16a34a)' }}>registered</span>
      ) : (
        <span className="text-xs" style={{ color: 'var(--warn, #d97706)' }}>none</span>
      ),
  },
  {
    key: 'ok',
    header: 'Result',
    cell: (r) => (
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          padding: '2px 8px',
          borderRadius: 6,
          color: r.ok ? '#16a34a' : '#dc2626',
          background: r.ok ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)',
        }}
      >
        {r.ok ? 'delivered' : 'failed'}
      </span>
    ),
  },
  {
    key: 'reason',
    header: 'Reason',
    cell: (r) => <span className="text-xs text-muted">{reasonOf(r)}</span>,
  },
];

/** Push Log ("God View") — every FCM push attempt + its result, written by
 *  notification-svc: who it went to, whether the device had a registered token,
 *  whether it delivered, and the real FCM/APNs reason on failure (UNREGISTERED,
 *  INVALID_ARGUMENT, or a 401 auth wedge). Auto-refreshes every 5s.
 *  Gated on notifications.read. */
export function PushLog() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'push-log'],
    queryFn: ({ signal }) => adminApi.pushLog(200, signal),
    refetchInterval: 5000,
  });

  const items = data?.items ?? [];
  const failed = items.filter((r) => !r.ok).length;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Push Log"
        subtitle={
          items.length
            ? `Last ${items.length} push attempts · ${failed} failed (auto-refreshes)`
            : 'Every FCM push attempt + delivery result (auto-refreshes)'
        }
      />
      {isLoading ? (
        <Spinner label="Loading push log…" />
      ) : isError ? (
        <EmptyState title="Couldn't load push log" description={(error as Error)?.message} />
      ) : (
        <DataTable<PushLogEntry>
          columns={columns}
          rows={items}
          rowKey={(r) => String(r.id)}
          stickyHeader
          empty={
            <EmptyState
              title="No pushes yet"
              description="Trigger a ride request or notification — every send shows up here within a few seconds."
            />
          }
        />
      )}
    </AdminPage>
  );
}
